import {LoginState, Role} from '../../constants/enumerations';
import {
  ResultState,
  SignUpState,
} from '../../constants/enumerations/state.enum';
import {IClient, INotificationDto} from '../../interfaces/dtos';

import {ISignupForm} from '../../interfaces/ui';

export interface IUiConfiguration {
  proofForm: {
    payment: boolean;
    vatTax: boolean;
    incomeTax: boolean;
  };
  geoLocation: {
    interval: number;
  };
}

export interface IProfile {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  identity: string;
  entity: string;
  role: Role;
  referenceId?: string;
}

export interface IBank {
  title: string;
  value: string;
  foreign: boolean;
  comission?: {
    percent: number;
    fixed: number;
  };
}

export interface IListOption {
  title: string;
  value: string;
  source?: IBank;
}

/**
 *
 */
export interface IAuthSession {
  reset?: boolean | null;
  token?: string;
  begin?: number | null;
  lifetime?: string | null;
  signed?: boolean;
}

export interface IProductSubUnit {
  title: string;
  size: number;
}

export interface IPrice {
  value: number;
  units: {
    min: number;
    max: number;
  };
}

export interface IProductUnit {
  id: string;
  title: string;
  price: IPrice[];
  subUnit: IProductSubUnit;
}

export interface IProduct {
  id: string;
  title: string;
  summary: string;
  units: IProductUnit[];
  ivaPercent: number;
}

export interface IOrderTransport {
  driver: string;
  identity: string;
  plate: string;
  vehicle: string;
  hasTrailer: boolean;
  trailerPlate: string;
}

export interface IOrderItem {
  count: number;
  product: IProduct;
  unit: IProductUnit;
  price: number;
}

export interface IOrder {
  id: string | null;
  client: IClient;
  items: IOrderItem[] | null;
  transport: IOrderTransport | null;
  date: Date | null;
  paymentOptionId: string | null;
}

/**
 *
 */
export interface IState {
  tyc?: string;
  faq?: string;
  phone?: string;
  whatsApp?: string;
  email?: string;
  session?: IAuthSession;
  errors: any;
  entities: IListOption[];
  banks: IListOption[];
  profile: IProfile | null;
  notifications: INotificationDto[] | null;
  uiConfiguration: IUiConfiguration | null;
  isOutdated: boolean | null;
  goToMessages: boolean;
  isLocalizationActive: boolean | null;
  remoteMessageError: string | null;
  sourceBanks: IListOption[];
}

/**
 *
 */
export interface IContext extends IState {
  signup: (formData: ISignupForm) => Promise<SignUpState>;
  signin: (login: string, password: string) => Promise<LoginState>;
  signOff: () => void;
  clearErrors: () => void;
  isAnonymous: () => boolean;
  isClient: () => boolean;
  isSubClient: () => boolean;
  isDefaultSubClient: () => boolean;
  isEnchancedSubClient: () => boolean;
  isSalesMan: () => boolean;
  fetchEntities: () => Promise<boolean>;
  fetchNotificationList: (read?: boolean) => Promise<boolean>;
  markNotificationsRead: () => void;
  recoverPassword: (email: string) => Promise<ResultState>;
  changePassword: (password: string) => Promise<ResultState>;
  removeAccount: () => Promise<ResultState>;
  fetchTyc: () => Promise<ResultState>;
  getDashboardScreenName: () => 'Dashboard' | 'DashboardClient';
  checkIsOutdated: () => Promise<ResultState>;
  addNotification: (title: string, body: string) => void;
  setGoToMessages: (value: boolean) => void;
  registerDevice: (
    email: string,
    token: string,
    device: string,
  ) => Promise<boolean>;
  isOpenedWebSession: () => boolean;
  signinWebSession: () => void;
  actions?: typeof ACTION_VERBS;
  getToken(): string;
  setLocalizationActive(active: boolean): void;
  saveState: () => Promise<void>;
  loadState: () => Promise<void>;
  clearStorage: () => Promise<void>;
  isConnected: () => boolean;
  setRemoteMessageError: (msg: string | null) => void;
  sendEmailBalance: (id: string, typeCode: string) => Promise<ResultState>;
}

/**
 *
 */
export const ACTION_VERBS = {
  BALANCE_LIST: 'BALANCE_LIST',
  NOTIFICATION_LIST: 'NOTIFICATION_LIST',
  ENTITIES: 'ENTITIES',
  SIGN_IN: 'SIGN_IN',
  STATE_PROCESS_ERROR: 'STATE_PROCESS_ERROR',
  SIGN_OUT: 'SIGN_OUT',
  CHANGE_PASSWORD: 'CHANGE_PASSWORD',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SIGN_UP: 'SIGN_UP',
  SEND_PROOF: 'SEND_PROOF',
  RECOVER_PASSWORD: 'RECOVER_PASSWORD',
  SET_AUTHENTICATED: 'SET_AUTHENTICATED',
  SEND_EMAIL_BALANCE: 'SEND_EMAIL_BALANCE',
  GET_TYC: 'GET_TYC',
  BALANCE_SHEET: 'BALANCE_SHEET',
  BALANCE_SHEET_SUBCLIENT: 'BALANCE_SHEET_SUBCLIENT',
  BALANCE_SHEET_SALESMAN: 'BALANCE_SHEET_SALESMAN',
  REMOVE_ACCOUNT: 'REMOVE_ACCOUNT',
  HISTORY: 'HISTORY',
  DOCUMENT_LIST: 'DOCUMENT_LIST',
  SEMAPHORE_ITEMS: 'SEMAPHORE_ITEMS',
  DEBTOR_LIST: 'DEBTOR_LIST',
  RECEIVABLE_LIST: 'RECEIVABLE_LIST',
  IS_OUTDATED: 'IS_OUTDATED',
  REGISTER_DEVICE: 'REGISTER_DEVICE',
  APP_INFO: 'APP_INFO',
  MARK_NOTIFICATIONS_READ: 'MARK_NOTIFICATIONS_READ',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  GO_TO_MESSAGES: 'GO_TO_MESSAGES',
  GET_SALESMAN_CLIENTS: 'GET_SALESMAN_CLIENTS',
  GET_SALESMAN_PRODUCTS: 'GET_SALESMAN_PRODUCTS',
  SET_SALESMAN_PRODUCT_TO_ADD: 'SET_SALESMAN_PRODUCT_TO_ADD',
  UPDATE_SALESMAN_NEW_ORDER: 'UPDATE_SALESMAN_NEW_ORDER',
  UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART:
    'UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART',
  SEND_SALESMAN_NEW_ORDER: 'SEND_SALESMAN_NEW_ORDER',
  SET_SALESMAN_CLIENT: 'SET_SALESMAN_CLIENT',
  SET_LOCALIZATION_ACTIVE: 'SET_LOCALIZATION_ACTIVE',
  LOAD_STATE_FROM_STORE: 'LOAD_STATE_FROM_STORE',
  SET_REMOTE_MESSAGE_ERROR: 'SET_REMOTE_MESSAGE_ERROR',
};
